export const SOLID_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_unit;
layout(location = 1) in vec4 a_rect;
layout(location = 2) in vec4 a_color;

uniform vec2 u_resolution;
out vec4 v_color;

void main() {
  vec2 pixel = a_rect.xy + a_unit * a_rect.zw;
  vec2 clip = pixel / u_resolution * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  v_color = a_color;
}
`

export const SOLID_FRAGMENT_SHADER = `#version 300 es
precision mediump float;

in vec4 v_color;
out vec4 out_color;

void main() {
  out_color = v_color;
}
`

export const GLYPH_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_unit;
layout(location = 1) in vec4 a_rect;
layout(location = 2) in vec4 a_color;
layout(location = 3) in vec4 a_uv;

uniform vec2 u_resolution;
out vec4 v_color;
out vec2 v_uv;

void main() {
  vec2 pixel = a_rect.xy + a_unit * a_rect.zw;
  vec2 clip = pixel / u_resolution * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  v_color = a_color;
  v_uv = mix(a_uv.xy, a_uv.zw, a_unit);
}
`

export const GLYPH_FRAGMENT_SHADER = `#version 300 es
precision mediump float;

uniform sampler2D u_page;
in vec4 v_color;
in vec2 v_uv;
out vec4 out_color;

void main() {
  float coverage = texture(u_page, v_uv).r;
  out_color = vec4(v_color.rgb, v_color.a * coverage);
}
`

export const IMAGE_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_unit;
layout(location = 1) in vec4 a_rect;
layout(location = 3) in vec4 a_uv;

uniform vec2 u_resolution;
out vec2 v_uv;

void main() {
  vec2 pixel = a_rect.xy + a_unit * a_rect.zw;
  vec2 clip = pixel / u_resolution * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  v_uv = mix(a_uv.xy, a_uv.zw, a_unit);
}
`

export const IMAGE_FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform sampler2D u_image;
in vec2 v_uv;
out vec4 out_color;

void main() {
  out_color = texture(u_image, v_uv);
}
`

export function compileGridShader(gl: WebGL2RenderingContext, type: number,
                                  source: string): WebGLShader {
  const shader = gl.createShader(type)
  if (shader === null) throw new Error('WebGL shader allocation failed')
  try {
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const diagnostic = gl.getShaderInfoLog(shader) ?? 'no diagnostic'
      throw new Error(`WebGL shader compilation failed: ${diagnostic}`)
    }
    return shader
  } catch (error) {
    gl.deleteShader(shader)
    throw error
  }
}

export function linkGridProgram(gl: WebGL2RenderingContext, vertexSource: string,
                                fragmentSource: string): WebGLProgram {
  const vertex = compileGridShader(gl, gl.VERTEX_SHADER, vertexSource)
  let fragment: WebGLShader | null = null
  let program: WebGLProgram | null = null
  try {
    fragment = compileGridShader(gl, gl.FRAGMENT_SHADER, fragmentSource)
    program = gl.createProgram()
    if (program === null) throw new Error('WebGL program allocation failed')
    gl.attachShader(program, vertex)
    gl.attachShader(program, fragment)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const diagnostic = gl.getProgramInfoLog(program) ?? 'no diagnostic'
      throw new Error(`WebGL program linking failed: ${diagnostic}`)
    }
    return program
  } catch (error) {
    if (program !== null) gl.deleteProgram(program)
    throw error
  } finally {
    gl.deleteShader(vertex)
    if (fragment !== null) gl.deleteShader(fragment)
  }
}
